import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import './Carousel.css';
import { resolveImageUrl, ART_PLACEHOLDER_SVG } from '../../utils/imageUrl';

const GAP = 20;

/* ── Single shop card ──────────────────────────────────────────────── */

function ShopCard({ item, width, onClick }) {
  const banner = item.banner_url ? resolveImageUrl(item.banner_url) : null;
  const avatar = item.avatar_url ? resolveImageUrl(item.avatar_url) : null;
  const letter = (item.shop_name || item.artist_name || 'A')[0].toUpperCase();

  return (
    <div className="fc-card" style={{ width, minWidth: width }} onClick={() => onClick?.(item)}>
      {/* Glow border effect */}
      <div className="fc-card-glow" />

      {/* Banner */}
      <div className="fc-card-banner">
        {banner ? (
          <img src={banner} alt="" className="fc-card-banner-img"
            onError={e => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'block'; }} />
        ) : null}
        <div className="fc-card-banner-gradient" style={{ display: banner ? 'none' : 'block' }} />
        <div className="fc-card-scanline" />
      </div>

      {/* Body */}
      <div className="fc-card-body">
        <div className="fc-card-avatar-row">
          {avatar ? (
            <img src={avatar} alt="" className="fc-card-avatar"
              onError={e => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
          ) : null}
          <div className="fc-card-avatar-letter" style={{ display: avatar ? 'none' : 'flex' }}>{letter}</div>
          {item.listing_count > 0 && (
            <span className="fc-card-badge">{item.listing_count} work{item.listing_count !== 1 ? 's' : ''}</span>
          )}
        </div>

        <div className="fc-card-title">{item.shop_name || item.artist_name || 'Artist Shop'}</div>

        {item.artist_name && item.shop_name && item.shop_name !== item.artist_name && (
          <div className="fc-card-artist">by {item.artist_name}</div>
        )}

        {(item.bio || item.artist_name) && (
          <p className="fc-card-desc">{item.bio || `Explore artworks by ${item.artist_name}`}</p>
        )}

        {item.sample_images?.length > 0 && (
          <div className="fc-card-samples">
            {item.sample_images.slice(0, 4).map((img, i) => (
              <img key={i} src={resolveImageUrl(img)} alt="" className="fc-card-sample"
                onError={e => { e.target.onerror = null; e.target.src = ART_PLACEHOLDER_SVG; }} />
            ))}
          </div>
        )}

        <div className="fc-card-cta">Visit Shop <span className="fc-card-cta-arrow">&rarr;</span></div>
      </div>
    </div>
  );
}

/* ── Futuristic Carousel ───────────────────────────────────────────── */

export default function Carousel({
  items = [],
  autoplay = false,
  autoplayDelay = 4000,
  pauseOnHover = true,
  onItemClick,
}) {
  const wrapRef = useRef(null);
  const [wrapW, setWrapW] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [page, setPage] = useState(0);

  /* ① Measure usable width (subtract arrow area) */
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const gutter = window.innerWidth <= 600 ? 72 : 96;
      const measured = rect.width - gutter;
      if (measured > 0) {
        setWrapW(measured);
      }
    };
    // Initial measurement
    measure();
    // Force re-measurement after paint to catch accurate dimensions
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(measure);
    });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, []);

  /* ② Responsive column count */
  const cols = useMemo(() => {
    if (wrapW < 480) return 1;
    if (wrapW < 780) return 2;
    return 3;
  }, [wrapW]);

  const cardW = useMemo(() => {
    if (wrapW <= 0) return 300;
    return Math.floor((wrapW - GAP * (cols - 1)) / cols);
  }, [wrapW, cols]);

  const totalPages = Math.max(1, Math.ceil(items.length / cols));

  /* ③ Reset page on data/column change */
  useEffect(() => { setPage(0); }, [items.length, cols]);

  const goTo  = useCallback(p => setPage(Math.max(0, Math.min(p, totalPages - 1))), [totalPages]);
  const next  = useCallback(() => setPage(p => (p + 1) % totalPages), [totalPages]);
  const prev  = useCallback(() => setPage(p => (p - 1 + totalPages) % totalPages), [totalPages]);

  /* ④ Autoplay */
  useEffect(() => {
    if (!autoplay || totalPages <= 1) return;
    if (pauseOnHover && hovered) return;
    const id = setInterval(next, autoplayDelay);
    return () => clearInterval(id);
  }, [autoplay, autoplayDelay, next, hovered, pauseOnHover, totalPages]);

  /* ⑤ Compute translateX */
  const trackW = items.length * cardW + Math.max(0, items.length - 1) * GAP;
  const vpW = wrapW > 0 ? wrapW : 300;
  const pageShift = page * (cols * cardW + cols * GAP);
  const maxShift = Math.max(0, trackW - vpW);
  const tx = -Math.min(pageShift, maxShift);
  const center = trackW < vpW ? (vpW - trackW) / 2 : 0;

  /* ⑥ Render */
  if (!items.length) return <div ref={wrapRef} className="fc-wrap" style={{ minHeight: 380 }} />;

  return (
    <div
      ref={wrapRef}
      className="fc-wrap"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative', visibility: wrapW > 0 ? 'visible' : 'visible' }}
    >
      {totalPages > 1 && (
        <>
          <button className="fc-arrow fc-arrow-l" onClick={prev} aria-label="Previous">&#8249;</button>
          <button className="fc-arrow fc-arrow-r" onClick={next} aria-label="Next">&#8250;</button>
        </>
      )}

      <div className="fc-viewport">
        <div
          className="fc-track"
          style={{
            transform: `translateX(${tx + center}px)`,
            gap: `${GAP}px`,
          }}
        >
          {items.map((item, i) => (
            <ShopCard key={item?.artist_id ?? i} item={item} width={cardW} onClick={onItemClick} />
          ))}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="fc-dots">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              className={`fc-dot${page === i ? ' fc-dot-active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Page ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
